import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { UserProvider, useUserContext } from './context/UserContext';
import { TimelineProvider, useTimelineContext } from './context/TimelineContext';
import { TutorialProvider, useTutorialContext } from './context/TutorialContext';
import LoginPage from './components/LoginPage';
import TimelineGallery from './components/TimelineGallery';
import TimelinePage from './components/TimelinePage';
import TutorialOverlay from './components/TutorialOverlay';
import AdvancedTutorialPlaceholder from './components/AdvancedTutorialPlaceholder';
import { getTimelineFromImport } from './utils/timelineImport';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { currentUser } = useUserContext();
  return currentUser ? children : <Navigate to="/login" />;
};

const AppHeader = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentTimeline, events, activeTagFilter, setActiveTagFilter, allTags, renameCurrentTimeline } = useTimelineContext();
  const { importTimeline, userTimelines } = useUserContext();
  const { beginnerStepId, isBeginnerTutorialActive, openAdvancedTutorial, startBeginnerTutorial } = useTutorialContext();
  const [isRenaming, setIsRenaming] = useState(false);
  const [timelineNameDraft, setTimelineNameDraft] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuMessage, setMenuMessage] = useState('');
  const fileInputRef = useRef(null);
  const menuRef = useRef(null);
  
  const timelineRouteMatch = location.pathname.match(/^\/timeline\/([^/]+)\/?$/);
  const routeTimelineId = timelineRouteMatch ? Number(timelineRouteMatch[1]) : null;
  const routeTimeline = Number.isFinite(routeTimelineId)
    ? userTimelines.find(timeline => timeline.id === routeTimelineId)
    : null;
  const showHeader = Boolean(timelineRouteMatch);
  const showTimelineActions = Boolean(routeTimeline);
  const headerTimelineName = currentTimeline && routeTimeline && currentTimeline.id === routeTimeline.id
    ? currentTimeline.name
    : routeTimeline?.name;
  const isTutorialReviewingMenu = isBeginnerTutorialActive && beginnerStepId === 'menu-panel';

  const handleBackClick = () => {
    navigate('/gallery');
  };

  const handleFilterChange = (e) => {
    if (isTutorialReviewingMenu) return;
    setActiveTagFilter(e.target.value === 'all' ? null : e.target.value);
  };

  const getCurrentTimelineBackup = () => ({
    schemaVersion: 1,
    exportedAt: new Date().toISOString(),
    app: 'Agora Timeline',
    timeline: {
      ...currentTimeline,
      events,
      tags: allTags
    }
  });

  const getSafeFilename = (name) => {
    const safeName = (name || 'timeline')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return `${safeName || 'timeline'}-backup.json`;
  };

  const handleExportTimeline = () => {
    if (isTutorialReviewingMenu) return;
    if (!currentTimeline) return;

    const backup = getCurrentTimelineBackup();
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = getSafeFilename(currentTimeline.name);
    link.click();
    URL.revokeObjectURL(url);
    setMenuMessage('Exported current timeline.');
    setIsMenuOpen(false);
  };

  const handleImportTimeline = async (e) => {
    if (isTutorialReviewingMenu) return;
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const importedTimeline = getTimelineFromImport(JSON.parse(text));
      const importedTimelineId = importTimeline(importedTimeline);
      setMenuMessage('Imported timeline backup.');
      setIsMenuOpen(false);
      navigate(`/timeline/${importedTimelineId}`);
    } catch (error) {
      setMenuMessage(error.message || 'Could not import this backup file.');
    } finally {
      e.target.value = '';
    }
  };

  useEffect(() => {
    if (!isRenaming) {
      setTimelineNameDraft(headerTimelineName || '');
    }
  }, [headerTimelineName, isRenaming]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const handleDocumentMouseDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsMenuOpen(false);
      }
    };

    const handleDocumentKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    document.addEventListener('keydown', handleDocumentKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [isMenuOpen]);

  const handleRenameSubmit = (e) => {
    e.preventDefault();
    if (showTimelineActions && renameCurrentTimeline(timelineNameDraft)) {
      setIsRenaming(false);
    }
  };

  const handleRenameCancel = () => {
    setTimelineNameDraft(headerTimelineName || '');
    setIsRenaming(false);
  };

  if (!showHeader) return null;

  return (
    <div className="header">
      <div className="header-left">
        <button 
          className="home-link"
          onClick={handleBackClick}
          aria-label="Go to gallery"
        >
          <img src="/agora-temple.png" alt="" className="header-logo" aria-hidden="true" />
          <span>Agora Timeline</span>
        </button>
      </div>
      <div className="header-center">
        {!showTimelineActions ? (
          <div className="header-title-group">
            <h1>Timeline not found</h1>
          </div>
        ) : isRenaming ? (
          <form className="header-rename-form" onSubmit={handleRenameSubmit}>
            <input
              type="text"
              value={timelineNameDraft}
              onChange={(e) => setTimelineNameDraft(e.target.value)}
              aria-label="Timeline name"
              autoFocus
            />
            <button type="submit" disabled={!timelineNameDraft.trim()}>
              Save
            </button>
            <button type="button" onClick={handleRenameCancel}>
              Cancel
            </button>
          </form>
        ) : (
          <div className="header-title-group">
            <h1 data-tutorial-id="timeline-title">{headerTimelineName || 'Untitled Timeline'}</h1>
            <button
              type="button"
              className="header-rename-button"
              onClick={() => setIsRenaming(true)}
            >
              Rename
            </button>
          </div>
        )}
      </div>
      <div className="header-right">
        {showTimelineActions && (
          <div className="app-menu" ref={menuRef}>
            <button
              type="button"
              className="app-menu-button"
              data-tutorial-id="app-menu-button"
              onClick={() => {
                setIsMenuOpen(prev => !prev);
                setMenuMessage('');
              }}
              aria-expanded={isMenuOpen}
              aria-haspopup="menu"
              aria-label="Open app menu"
            >
              <span className="app-menu-icon" aria-hidden="true">
                <span></span>
                <span></span>
                <span></span>
              </span>
              <span>Menu</span>
            </button>
            {isMenuOpen && (
              <div className="app-menu-panel" role="menu" data-tutorial-id="app-menu-panel">
                <div className="app-menu-section">
                  <label htmlFor="tag-filter">Filter by tag</label>
                  <select
                    id="tag-filter"
                    value={activeTagFilter || 'all'}
                    onChange={handleFilterChange}
                    className="position-filter"
                    disabled={isTutorialReviewingMenu}
                  >
                    <option value="all">All Tags</option>
                    {allTags.map(tag => (
                      <option key={tag} value={tag}>{tag}</option>
                    ))}
                  </select>
                </div>
                <div className="app-menu-section">
                  <button type="button" onClick={handleExportTimeline} role="menuitem" disabled={isTutorialReviewingMenu}>
                    Export Timeline
                  </button>
                  <button type="button" onClick={() => !isTutorialReviewingMenu && fileInputRef.current?.click()} role="menuitem" disabled={isTutorialReviewingMenu}>
                    Import Timeline
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/json,.json"
                    className="app-menu-file-input"
                    onChange={handleImportTimeline}
                  />
                </div>
                <div className="app-menu-section">
                  <button
                    type="button"
                    onClick={() => {
                      if (isTutorialReviewingMenu) return;
                      setIsMenuOpen(false);
                      startBeginnerTutorial();
                    }}
                    role="menuitem"
                    disabled={isTutorialReviewingMenu}
                  >
                    Replay Beginner Tutorial
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (isTutorialReviewingMenu) return;
                      setIsMenuOpen(false);
                      openAdvancedTutorial();
                    }}
                    role="menuitem"
                    disabled={isTutorialReviewingMenu}
                  >
                    Advanced Tutorial
                  </button>
                </div>
                <div className="app-menu-section app-menu-placeholder" aria-label="Beta backup reminder">
                  Beta: timelines are saved locally in this browser. Export after important work to keep a backup.
                </div>
                {menuMessage && <p className="app-menu-message">{menuMessage}</p>}
              </div>
            )}
            {!isMenuOpen && menuMessage && <span className="app-menu-status">{menuMessage}</span>}
          </div>
        )}
      </div>
    </div>
  );
};

function App() {
  return (
    <Router>
      <UserProvider>
        <TimelineProvider>
          <TutorialProvider>
            <div className="app">
              <AppHeader />
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route 
                  path="/gallery" 
                  element={
                    <ProtectedRoute>
                      <TimelineGallery />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/timeline/:timelineId" 
                  element={
                    <ProtectedRoute>
                      <TimelinePage />
                    </ProtectedRoute>
                  } 
                />
                <Route path="/" element={<Navigate to="/gallery" />} />
              </Routes>
              <TutorialOverlay />
              <AdvancedTutorialPlaceholder />
            </div>
          </TutorialProvider>
        </TimelineProvider>
      </UserProvider>
    </Router>
  );
}

export default App;
